/* eslint-disable no-empty */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import Uppy from '@uppy/core';
import { useUppy, Dashboard } from '@uppy/react';
import AwsS3 from '@uppy/aws-s3';
import Audio from '@uppy/audio';
import { Input, Icon } from 'rsuite';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Flex,
  Box,
  Heading,
  Divider,
} from '@chakra-ui/react';
import { User } from '../../../@types';
import { ModalWrapper } from '../styles/ModalWrapper';
import * as Constants from '../../../constants';
import { showNotification } from '../../notification';
import { AudioResponseWrapper } from '../../episode-video/components/QuestionResponseView';
import { ResponseModalFooter } from './ResponseModalFooter';

import {
  useSubmitResponse,
  useCurrentUser,
  useUserPromptResponses,
  getSignedUrl,
} from '../../../api';

const ExistingWrapper = styled.div`
  height: 300px;
  display: flex;
  .video-container {
    width: 80%;
    height: 100%;
    margin: 0 auto;
    direction: ltr;

    video {
      width: 100%;
      object-fit: cover;
      border-radius: 0;
    }
  }
`;

export const AudioResponseModal: React.FC<{
  user: User;
  promptId: string;
  show: boolean;
  questionText: string;
  icon: React.ReactNode;
  closeModal: () => void;
  setShowAudioResponseModal: (show: boolean) => void;
}> = ({
  user,
  promptId,
  show,
  setShowAudioResponseModal,
  questionText,
  icon,
  closeModal,
}) => {
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [fileAdded, setFileAdded] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    data: responses,
    isLoading: responsesLoading,
  } = useUserPromptResponses(promptId, user.id);

  const myResponse = responses && responses[Constants.RESPONSE_TYPE_IDS.AUDIO];

  const {
    mutateAsync: submitResponse,
    isLoading,
    isError,
    error: submitErrorMessage,
  } = useSubmitResponse(promptId);

  const shouldSaveDescription = useRef(!myResponse);

  const [responseText, setResponseText] = useState(myResponse?.text || '');

  const handleInputText = (text: string) => {
    setResponseText(text);
  };

  const handleSubmitResponse = useCallback(
    async ({ shouldShowNotification = true, ...rest }) => {
      try {
        const res = await submitResponse({
          prompt_id: promptId,
          response_type: Constants.RESPONSE_TYPE_IDS.AUDIO,
          text: responseText,
          ...rest,
        });
        if (shouldShowNotification) {
          if (res.success) {
            showNotification({
              type: 'success',
              title: 'Success!',
              message: 'Your audio response was submitted!',
            });
          } else {
            showNotification({
              type: 'error',
              title: 'Oh no!',
              message:
                'There was a problem uploading your audio. Please try again.',
            });
          }
        }
        return res;
      } catch (e) {
        //
      }
      return false;
    },
    [promptId, Constants, showNotification, submitResponse, responseText]
  );

  const handleClose = () => {
    setShowAudioResponseModal(false);
    onClose();
  };

  useEffect(() => {
    const saveDescription = async () => {
      await handleSubmitResponse({
        shouldShowNotification: false,
      });
    };
    if (uploadSuccess && !!shouldSaveDescription.current) {
      shouldSaveDescription.current = false;
      saveDescription();
    }
  }, [uploadSuccess]);

  const uppyAudio = useUppy(() => {
    return new (Uppy as any)({
      id: 'uppy-audio',
      autoProceed: false,
      // debug: true,
      restrictions: {
        maxNumberOfFiles: 1,
        minNumberOfFiles: 1,
        allowedFileTypes: ['audio/*'],
      },
      meta: {
        user_id: user.id,
        response_type: Constants.RESPONSE_TYPE_IDS.AUDIO.toString(),
      },
      onBeforeFileAdded: (currentFile: any, files: any) => {
        setFileAdded(true);
      },
    })
      .use(Audio, {
        preferredAudioMimeType: 'audio/webm',
      })
      .use(AwsS3, {
        async getUploadParameters(file: any) {
          const {
            url,
            base_path,
            full_path,
            base_name,
            full_name,
          } = await getSignedUrl(promptId, file.extension);

          uppyAudio.setMeta({
            base_path,
            full_path,
            base_name,
            full_name,
          });
          return {
            url,
            method: 'PUT',
            headers: {
              contentType: file.type,
            },
          };
        },
      })
      .on('upload-success', async (file: any, response: any) => {
        const { base_path, full_path, base_name, full_name } = file.meta;
        const submit = await handleSubmitResponse({
          base_path,
          full_path,
          base_name,
          full_name,
        });
        setUploadSuccess(submit.success);
      });
  });

  useEffect(() => {
    if (show) onOpen();
  }, [show]);

  if (!user || responsesLoading) return null;

  const { message: errorMessage = '' } = (submitErrorMessage as any) || {};
  const strippedText = questionText.replace(/<[^>]+>/g, '');

  return (
    <Modal size="3xl" isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalWrapper>
        <ModalContent className="audio-response">
          <ModalHeader>{icon} Respond with audio</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl isInvalid={isError}>
              <FormLabel>
                <div
                  className="question-text"
                  dangerouslySetInnerHTML={{ __html: strippedText }}
                />
              </FormLabel>
              <Flex textAlign="center" align="center">
                <Box w="100%">
                  <Heading
                    my={4}
                    color="gray.500"
                    fontWeight="semibold"
                    fontSize="md"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon size="2x" icon="import" />
                    <Box ml={2} as="span">
                      {!myResponse
                        ? 'Record Your Audio'
                        : 'Your Audio Response'}
                    </Box>
                  </Heading>
                  {uppyAudio && !myResponse && (
                    <Dashboard
                      uppy={uppyAudio}
                      proudlyDisplayPoweredByUppy={false}
                      disableLocalFiles
                      plugins={['Audio']}
                    />
                  )}
                  {!!myResponse && (
                    <ExistingWrapper>
                      <AudioResponseWrapper response={myResponse} />
                    </ExistingWrapper>
                  )}
                </Box>
              </Flex>
              <Box mt={8} px={14}>
                <FormLabel fontSize="sm">Description:</FormLabel>
                <Textarea
                  placeholder="Enter a description here..."
                  size="md"
                  onChange={(e) => handleInputText(e.target.value)}
                  value={responseText}
                />
                <FormErrorMessage>{`${errorMessage}`}</FormErrorMessage>
              </Box>
            </FormControl>
          </ModalBody>

          <ResponseModalFooter
            myResponse={myResponse}
            handleClose={handleClose}
            fileAdded={fileAdded}
            isLoading={isLoading}
            isDisabled={!myResponse && !uploadSuccess}
            onSubmit={handleSubmitResponse}
            onDelete={() => null}
          />
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};
